import { useNavigate } from 'react-router-dom'
import { DocsCallout, DocsContent, DocsDiagram, DocsLayout, H2 } from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsPipelines() {
  return (
    <DocsLayout>
      <DocsContent
        title="Phase 2 Pipelines"
        description="Understand XENDREL's Phase 2 extraction pipelines that transform indexed documents into structured knowledge — entities, relationships, and queryable data."
        previousPage={{ title: 'Document Processing', href: '/docs/document-processing' }}
        nextPage={{ title: 'Text Extraction', href: '/docs/pipelines/text' }}
      >
        <H2 id="phase1-vs-phase2">Phase 1 vs Phase 2</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Document processing in XENDREL happens in two distinct phases:
        </p>

        <div className="space-y-4 my-6">
          <PhaseCard
            phase="Phase 1 — Indexing"
            description="Ingests the raw file, detects its type and structure, generates searchable text chunks, and converts tabular data to Parquet format on S3. This phase makes the document searchable and prepares it for extraction."
            output="Parquet file, text chunks, document analysis metadata"
          />
          <PhaseCard
            phase="Phase 2 — Extraction"
            description="Takes the indexed data from Phase 1 and transforms it into structured knowledge. The extraction strategy depends on the pipeline type — text documents go through LLM entity extraction, tabular data through column mapping, and geospatial data through a specialized remapping pipeline."
            output="Entities and relationships in Neo4j, or normalized Parquet files for time-series data"
          />
        </div>

        <DocsCallout variant="info" title="Automatic Pipeline Detection">
          XENDREL automatically detects the best pipeline for your data based on file type and
          content analysis. CSV files with coordinate columns are routed to the Geospatial pipeline,
          blockchain transaction files to the Tabular pipeline with blockchain patterns, and text
          documents to the Text Extraction pipeline. You can always override the auto-detected
          pipeline.
        </DocsCallout>

        <H2 id="data-flow">Data Flow</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The following diagram shows how data flows through both phases:
        </p>

        <DocsDiagram>{`Upload → Phase 1 (Indexing)
           ├─ Parse file → generate text chunks
           ├─ CSV/XLSX → Parquet on S3
           ├─ Detect data type (text/tabular/geospatial)
           └─ Store analysis metadata in PostgreSQL
                    ↓
         Phase 2 (Extraction) — pipeline-specific:
           ├─ Text Pipeline      → LLM entity extraction → Neo4j
           ├─ Tabular Pipeline   → Column mapping → row-by-row → Neo4j
           └─ Geospatial Pipeline → Column mapping → remapped Parquet + entities → Neo4j`}</DocsDiagram>

        <H2 id="pipeline-types">Pipeline Types</H2>

        <p className="text-foreground mb-6 leading-relaxed">
          Each pipeline is optimized for a specific data shape. Choose the one that matches your
          source data:
        </p>

        <div className="space-y-4 my-6">
          <PipelineCard
            title="Text Extraction"
            href="/docs/pipelines/text"
            description="For PDFs, Word documents, and other unstructured text. Uses LLM-powered entity extraction with the POLE schema to identify people, organizations, locations, events, and their relationships."
            formats=".pdf, .docx, .txt"
            output="Entities + relationships → Neo4j knowledge graph"
          />
          <PipelineCard
            title="Tabular Extraction"
            href="/docs/pipelines/tabular"
            description="For CSV and Excel files with structured data. LLM-driven column mapping proposes entity types, then rows are extracted one-by-one into the knowledge graph. Supports generic, blockchain, and financial patterns."
            formats=".csv, .xlsx"
            output="Entities + relationships → Neo4j knowledge graph"
          />
          <PipelineCard
            title="Geospatial Pipeline"
            href="/docs/pipelines/geospatial"
            description="For location tracking data — vehicle GPS trackers, cell tower CDRs, AIS ship tracking, and similar time-series geospatial files. Uses a hybrid storage model: entities go to Neo4j, observations stay in optimized Parquet files queried by DuckDB."
            formats=".csv (GPS, CDR, AIS)"
            output="Entities → Neo4j, track points → remapped Parquet → DuckDB"
          />
        </div>

        <H2 id="schema-driven-extraction">Schema-Driven Extraction</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          All Phase 2 pipelines are driven by the{' '}
          <strong className="text-foreground">Extraction Schema</strong> (POLE Schema). This schema
          defines:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity Types</strong> — Person, Vehicle, Phone,
              Location, Company, BankAccount, etc.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Relationship Types</strong> — OWNS, HAS_PHONE,
              CONTACTED, PARTICIPATED_IN, OCCURRED_AT, etc.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Valid Patterns</strong> — Which entity types can
              connect via which relationships, e.g. (Person)-[HAS_PHONE]-&gt;(Phone)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Property Types</strong> — Expected properties on
              each entity type with data types (STRING, FLOAT, DATETIME, POINT)
            </span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          The schema is versioned and can be customized per case. Navigate to the{' '}
          <strong className="text-foreground">Schema</strong> page in the app to view and manage
          extraction schemas.
        </p>

        <DocsCallout variant="tip" title="Pipeline Selection">
          For most investigations, you will use a mix of pipelines across different documents. A
          single case might have PDFs processed with Text Extraction, bank statement CSVs with
          Tabular Extraction, and GPS tracker exports with the Geospatial Pipeline — all feeding
          into the same knowledge graph.
        </DocsCallout>
      </DocsContent>
    </DocsLayout>
  )
}

function PhaseCard({
  phase,
  description,
  output,
}: {
  phase: string
  description: string
  output: string
}) {
  return (
    <Card size="compact" className="rounded-lg p-4 bg-card/50">
      <div className="font-semibold text-foreground mb-2">{phase}</div>
      <p className="text-sm text-muted-foreground mb-2">{description}</p>
      <div className="text-xs text-muted-foreground">
        <span className="text-amber-500/70">Output:</span> {output}
      </div>
    </Card>
  )
}

function PipelineCard({
  title,
  href,
  description,
  formats,
  output,
}: {
  title: string
  href: string
  description: string
  formats: string
  output: string
}) {
  const navigate = useNavigate()

  return (
    <Card
      asChild
      interactive
      size="compact"
      className="rounded-lg p-4 bg-card/50 hover:border-amber-500/30 hover:bg-card"
    >
      <button
        type="button"
        onClick={() => {
          navigate(href)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }}
        className="block w-full text-left"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-foreground">{title}</span>
          <span className="text-xs text-muted-foreground font-mono">{formats}</span>
        </div>
        <p className="text-sm text-muted-foreground mb-2">{description}</p>
        <div className="text-xs text-muted-foreground">
          <span className="text-amber-500/70">Output:</span> {output}
        </div>
      </button>
    </Card>
  )
}
